import { useEffect, useRef, useState } from 'react'
import { Bell, X, CheckCheck, AlertTriangle, Info, CheckCircle, AlertCircle, Zap, ArrowRight, Bot, TrendingUp, ShieldAlert, Settings } from 'lucide-react'
import { useNotifications } from '@/hooks/useNotifications'
import type { NotifSeverity, NotifPriority, NotifType } from '@/hooks/useNotifications'
import { formatDistanceToNow } from 'date-fns'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'

const SEVERITY_CONFIG: Record<NotifSeverity, { icon: React.ElementType; cls: string }> = {
  info:     { icon: Info,          cls: 'text-blue-light  bg-blue/10   border-blue/20' },
  success:  { icon: CheckCircle,   cls: 'text-emerald     bg-emerald/10 border-emerald/20' },
  warning:  { icon: AlertTriangle, cls: 'text-amber       bg-amber/10  border-amber/20' },
  critical: { icon: AlertCircle,   cls: 'text-rose-light  bg-rose/10   border-rose/20' },
}

/** Priority-based left-border accent for each notification row */
const PRIORITY_BORDER: Record<NotifPriority, string> = {
  urgent: 'border-l-rose',
  high:   'border-l-amber',
  medium: 'border-l-blue',
  low:    'border-l-border-light',
}

/** Severity-based left-border for smart alerts */
const SEVERITY_BORDER: Record<NotifSeverity, string> = {
  critical: 'border-l-rose',
  warning:  'border-l-amber',
  info:     'border-l-blue',
  success:  'border-l-emerald',
}

const SOURCE_COLORS: Record<string, string> = {
  ARIA:        'badge-purple',
  Risk:        'badge-red',
  Portfolio:   'badge-blue',
  Compliance:  'badge-amber',
  Market:      'badge-blue',
  SmartAlert:  'badge-amber',
}

/** Icon for each notification type shown in the dropdown preview */
const TYPE_ICON: Record<NotifType, React.ElementType> = {
  recommendation: TrendingUp,
  trade:          Zap,
  alert:          ShieldAlert,
  risk:           AlertTriangle,
  system:         Settings,
}

type Tab = 'all' | 'alerts'

interface DrawerProps {
  open: boolean
  onClose: () => void
}

// ── Compact notification dropdown (desktop top-bar) ──────────────────────────

/**
 * NotificationDropdown — bell icon with unread badge that opens a compact
 * dropdown showing the 5 most-recent notifications plus a "View all" link.
 * Use this in the desktop top bar. The full drawer is still accessible via
 * the "View all" link.
 */
export function NotificationDropdown({ onViewAll }: { onViewAll: () => void }) {
  const count = useNotifications(s => s.unreadCount)
  const notifications = useNotifications(s => s.notifications)
  const markRead = useNotifications(s => s.markRead)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  const recent = notifications.slice(0, 5)

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="relative btn-ghost p-2"
        aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
        aria-expanded={open ? 'true' : 'false'}
        aria-haspopup="dialog"
      >
        <Bell size={18} />
        {count > 0 && (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose text-white text-[10px] font-bold flex items-center justify-center"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 glass bg-surface-DEFAULT/90
                     rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in"
          role="dialog"
          aria-label="Recent notifications"
          aria-modal="false"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-text-muted" aria-hidden />
              <span className="text-sm font-semibold text-text-primary">Notifications</span>
              {count > 0 && (
                <span className="badge badge-rose text-[10px] py-0 px-1.5">{count}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="btn-ghost p-1"
              aria-label="Close notifications"
            >
              <X size={13} />
            </button>
          </div>

          {/* Notification list */}
          {recent.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-text-muted">
              No notifications
            </p>
          ) : (
            <ul className="divide-y divide-border" aria-label="Recent notifications list">
              {recent.map(n => {
                const Icon = TYPE_ICON[n.type] ?? Info
                const { cls } = SEVERITY_CONFIG[n.severity]
                return (
                  <li
                    key={n.id}
                    className={clsx(
                      'px-4 py-3 flex items-start gap-3 cursor-pointer transition-colors',
                      !n.read ? 'bg-surface-card hover:bg-surface-hover' : 'hover:bg-surface-card',
                    )}
                    onClick={() => { markRead(n.id); setOpen(false) }}
                  >
                    {/* Category icon */}
                    <div className={clsx('w-7 h-7 rounded-md border flex items-center justify-center shrink-0 mt-0.5', cls)}>
                      <Icon size={12} aria-hidden />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={clsx(
                        'text-xs font-medium truncate',
                        n.read ? 'text-text-secondary' : 'text-text-primary',
                      )}>
                        {n.title}
                      </p>
                      <p className="text-[11px] text-text-muted mt-0.5 leading-snug line-clamp-2">
                        {n.message}
                      </p>
                      <p className="text-[10px] text-text-muted mt-1">
                        {formatDistanceToNow(n.timestamp, { addSuffix: true })}
                      </p>
                    </div>

                    {/* Unread dot */}
                    {!n.read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue mt-1.5 shrink-0" aria-hidden />
                    )}
                  </li>
                )
              })}
            </ul>
          )}

          {/* Footer — view all */}
          <div className="border-t border-border px-4 py-2.5 flex items-center justify-between">
            <Link
              to="/app/alerts"
              className="text-xs text-blue-light hover:text-blue transition-colors flex items-center gap-1"
              onClick={() => setOpen(false)}
            >
              View Smart Alerts
              <ArrowRight size={11} aria-hidden />
            </Link>
            <button
              type="button"
              onClick={() => { setOpen(false); onViewAll() }}
              className="text-xs text-text-muted hover:text-text-secondary transition-colors flex items-center gap-1"
            >
              All notifications
              <ArrowRight size={11} aria-hidden />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Simple bell (used in mobile nav — just opens the full drawer) ─────────────

export function NotificationBell({ onClick }: { onClick: () => void }) {
  const count = useNotifications(s => s.unreadCount)
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative btn-ghost p-2"
      aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
    >
      <Bell size={18} />
      {count > 0 && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose text-white text-[10px] font-bold flex items-center justify-center"
        >
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  )
}

export default function NotificationDrawer({ open, onClose }: DrawerProps) {
  const { notifications, markRead, markAllRead, dismiss } = useNotifications()
  const drawerRef = useRef<HTMLDivElement>(null)
  const [tab, setTab] = useState<Tab>('all')

  // Trap focus inside drawer
  useEffect(() => {
    if (open) drawerRef.current?.focus()
  }, [open])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  if (!open) return null

  const smartAlerts = notifications.filter(
    n => n.type === 'alert' && n.data?.alert_type,
  )
  const unreadAlerts = smartAlerts.filter(n => !n.read).length

  const displayed = tab === 'alerts' ? smartAlerts : notifications

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        tabIndex={-1}
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-sm glass bg-surface-DEFAULT/90
                   flex flex-col shadow-2xl animate-slide-in focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-text-muted" aria-hidden />
            <h2 className="font-semibold text-text-primary">Notifications</h2>
            {notifications.filter(n => !n.read).length > 0 && (
              <span className="badge badge-blue">{notifications.filter(n => !n.read).length}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={markAllRead}
              className="btn-ghost text-xs gap-1.5 py-1"
              aria-label="Mark all as read"
            >
              <CheckCheck size={13} aria-hidden /> All read
            </button>
            <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close notifications">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-5 gap-4">
          <button
            type="button"
            onClick={() => setTab('all')}
            className={clsx(
              'py-2.5 text-xs font-medium border-b-2 transition-colors',
              tab === 'all'
                ? 'border-blue text-blue-light'
                : 'border-transparent text-text-muted hover:text-text-secondary',
            )}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setTab('alerts')}
            className={clsx(
              'py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5',
              tab === 'alerts'
                ? 'border-amber text-amber'
                : 'border-transparent text-text-muted hover:text-text-secondary',
            )}
          >
            <Zap size={11} aria-hidden />
            Smart Alerts
            {unreadAlerts > 0 && (
              <span className="badge badge-amber text-[10px] py-0 px-1">{unreadAlerts}</span>
            )}
          </button>
        </div>

        {/* List */}
        <ul className="flex-1 overflow-y-auto divide-y divide-border" aria-live="polite">
          {displayed.length === 0 && (
            <li className="px-5 py-12 text-center text-text-muted text-sm">
              {tab === 'alerts'
                ? 'No smart alerts have fired yet.'
                : 'No notifications'}
            </li>
          )}
          {displayed.map(n => {
            const { icon: Icon, cls } = SEVERITY_CONFIG[n.severity]
            // Smart alerts use severity border; others use priority border
            const borderCls = n.data?.alert_type
              ? (SEVERITY_BORDER[n.severity] ?? 'border-l-border-light')
              : (PRIORITY_BORDER[n.priority] ?? 'border-l-border-light')
            return (
              <li
                key={n.id}
                className={clsx(
                  'px-5 py-4 flex items-start gap-3 transition-colors border-l-2 cursor-pointer',
                  borderCls,
                  !n.read ? 'bg-surface-card hover:bg-surface-hover' : 'hover:bg-surface-card',
                )}
                onClick={() => markRead(n.id)}
              >
                {/* Icon */}
                <div className={clsx('w-8 h-8 rounded-lg border flex items-center justify-center shrink-0', cls)}>
                  <Icon size={14} aria-hidden />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={clsx('text-sm font-medium', n.read ? 'text-text-secondary' : 'text-text-primary')}>
                      {n.title}
                    </p>
                    <button
                      onClick={e => { e.stopPropagation(); dismiss(n.id) }}
                      className="text-text-muted hover:text-text-primary shrink-0 mt-0.5"
                      aria-label="Dismiss notification"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5 leading-relaxed">{n.message}</p>

                  {/* Tickers row for smart alerts */}
                  {(() => {
                    const d = n.data as Record<string, unknown> | null
                    const tickers = d?.tickers as string[] | undefined
                    if (!d?.alert_type || !Array.isArray(tickers) || tickers.length === 0) return null
                    return (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tickers.slice(0, 6).map(t => (
                        <span key={t} className="badge badge-blue text-[10px] py-0">{t}</span>
                      ))}
                    </div>
                    )
                  })()}

                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`badge text-[10px] ${SOURCE_COLORS[n.source] ?? 'badge-blue'}`}>
                      {n.source}
                    </span>
                    <span className="text-[10px] text-text-muted">
                      {formatDistanceToNow(n.timestamp, { addSuffix: true })}
                    </span>
                    {!n.read && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue shrink-0" aria-label="Unread" />
                    )}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>

        {/* Footer hint for alerts tab */}
        {tab === 'alerts' && (
          <div className="px-5 py-3 border-t border-border text-[10px] text-text-muted">
            Smart alerts run every 15 min. Type <code className="font-mono">/alerts</code> in the terminal for details.
          </div>
        )}
      </div>
    </>
  )
}
